/**
 * Budget Line Item Dialog
 *
 * Modal dialog for creating and editing budget line items
 * Follows exact Figma design specifications from node 3574-8886
 *
 * Design specs:
 * - Background: #15203C
 * - Border radius: 20px
 * - Padding: 30px horizontal, 40px vertical
 * - Gap between sections: 40px
 * - Input height: 59px
 * - Label to input gap: 18px
 * - Field containers gap: 14px
 */

import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { X, Check, Upload, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import CurrencyInput from "@/components/ui/currency-input";
import { InputFile } from "@/components/ui/input-file";
import { budgetSubitemSchema, BudgetSubitemFormData } from "./schemas";
import { cn } from "@/lib/class-control";

export interface BudgetSubitemDialogProps {
  open: boolean;
  mode: "create" | "edit";
  phaseId: string;
  topicId: string;
  topicName: string;
  itemId?: string;
  initialData?: BudgetSubitemFormData;
  phases?: { id: string; name: string }[];
  onClose: () => void;
  onSave: (data: BudgetSubitemFormData) => Promise<void>;
  onDelete?: () => void;
  isSaving?: boolean;
}

const PAYMENT_METHODS = ["Pix", "TED", "DOC", "Boleto", "Cartão"] as const;
const STATUS_OPTIONS = ["Pago", "Pendente", "Cancelado", "Em Processamento"] as const;

export function BudgetSubitemDialog({
  open,
  mode,
  phaseId,
  topicId,
  topicName,
  itemId,
  initialData,
  phases = [],
  onClose,
  onSave,
  onDelete,
  isSaving = false,
}: BudgetSubitemDialogProps) {
  // Initialize form with react-hook-form + zod validation
  const form = useForm<BudgetSubitemFormData>({
    resolver: zodResolver(budgetSubitemSchema),
    defaultValues: initialData
      ? {
        phaseId: phaseId,
        name: initialData.name,
        value: initialData.value || 0,
      }
      : {
        phaseId: phaseId,
        name: "",
        value: 0,
      },
  });

  // Reset form whenever the dialog opens so fields are always up-to-date
  useEffect(() => {
    if (open && mode === "edit" && initialData) {
      form.reset({
        phaseId: initialData.phaseId ?? phaseId,
        name: initialData.name,
        value: initialData.value || 0,
      });
    } else if (open && mode === "create") {
      form.reset({
        phaseId: phaseId,
        name: "",
        value: 0,
      });
    } else if (!open) {
      form.reset({
        phaseId: phaseId,
        name: "",
        value: 0,
      });
    }
  }, [open, mode, phaseId, initialData]);

  // Handle form submission
  const handleSubmit = form.handleSubmit(async (data) => {
    await onSave(data);
    form.reset();
  });

  // Handle dialog close
  const handleClose = () => {
    if (!isSaving) {
      form.reset();
      onClose();
    }
  };

  const dialogTitle = mode === "edit"
    ? `Editando ${initialData?.name || form.watch("name") || ""}`
    : `Novo sub-item`;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[964px] bg-[#15203C] border-[#7846FF] rounded-[20px] px-[30px] py-[40px] overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between gap-[533px] mb-[40px]">
          <DialogTitle asChild>
            <h2 className="font-['Plus_Jakarta_Sans'] font-bold text-[24px] text-white flex-1">
              {dialogTitle}
            </h2>
          </DialogTitle>
          <DialogClose
            onClick={handleClose}
            disabled={isSaving}
            className="flex items-center gap-[10px] text-[#F6F6F6] hover:text-white transition-colors disabled:opacity-50"
          >
            <p className="font-['Plus_Jakarta_Sans'] font-semibold text-[16px]">
              Fechar
            </p>
            <X size={24} />
          </DialogClose>
        </div>

        {/* Divider */}
        <div className="h-px border-t border-gray-700 mb-[40px]" />

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-[40px]">

          {/* Fase do Projeto */}
          <div className="flex flex-col gap-[18px]">
            <label className="font-['Plus_Jakarta_Sans'] font-bold text-[16px] text-white">
              Fase do Projeto
            </label>
            <Controller
              name="phaseId"
              control={form.control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={isSaving}
                >
                  <SelectTrigger
                    className={cn(
                      "bg-[#15203C] border-[#5D5D5D] border-solid h-[59px] rounded-[10px] px-[20px] py-[10px]",
                      "text-[#D1D1D1] text-[14px] font-['Plus_Jakarta_Sans'] font-normal",
                      "focus-within:ring-2 focus-within:ring-[#7846FF]"
                    )}
                  >
                    <SelectValue placeholder="Selecione a fase" />
                  </SelectTrigger>
                  <SelectContent>
                    {phases.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {form.formState.errors.phaseId && (
              <p className="text-red-500 text-sm">{form.formState.errors.phaseId.message}</p>
            )}
          </div>

          {/* Descrição do sub-item - Full Width */}
          <div className="flex flex-col gap-[18px]">
            <label className="font-['Plus_Jakarta_Sans'] font-bold text-[16px] text-white">
              Descrição do sub-item
            </label>
            <Input
              {...form.register("name")}
              placeholder="Descrição do sub-item"
              disabled={isSaving}
              className={cn(
                "bg-[#15203C] border-[#5D5D5D] border-solid h-[59px] rounded-[10px] px-[20px] py-[10px]",
                "text-[#D1D1D1] text-[14px] font-['Plus_Jakarta_Sans'] font-normal",
                "placeholder:truncate placeholder:overflow-hidden placeholder:whitespace-nowrap",
                "focus-within:ring-2 focus-within:ring-[#7846FF]",
                form.formState.errors.name && "border-red-500"
              )}
            />
            {form.formState.errors.name && (
              <p className="text-red-500 text-sm">{form.formState.errors.name.message}</p>
            )}
          </div>

          {/* Fields Removed: Date and Payment method */}

          {/* Valor */}
          <div className="flex-1 flex flex-col gap-[18px]">
            <label className="font-['Plus_Jakarta_Sans'] font-bold text-[16px] text-white">
              Valor
            </label>
            <Controller
              name="value"
              control={form.control}
              render={({ field }) => (
                <CurrencyInput
                  field={field}
                  placeholder="R$ 0,00"
                  disabled={isSaving}
                  className="h-[59px]"
                />
              )}
            />
            {form.formState.errors.value && (
              <p className="text-red-500 text-sm">{form.formState.errors.value.message}</p>
            )}
          </div>

          {/* Fields Removed: Orçamento Previsto and Anexar Comprovante */}

          {/* Action Buttons */}
          <div className="flex justify-between items-center">
            {/* Delete Button (Left) */}
            <div>
              {mode === "edit" && onDelete && (
                <Button
                  type="button"
                  onClick={onDelete}
                  disabled={isSaving}
                  variant="ghost"
                  className={cn(
                    "h-[50px] px-4 rounded-[10px]",
                    "flex items-center justify-center gap-2",
                    "font-['Plus_Jakarta_Sans'] font-bold text-[16px] text-[#DC4426] hover:text-[#c23a1f] hover:bg-transparent",
                    "disabled:opacity-50 disabled:cursor-not-allowed"
                  )}
                >
                  <Trash2 size={20} />
                  Deletar
                </Button>
              )}
            </div>

            {/* Cancel/Save Buttons (Right) */}
            <div className="flex gap-[20px]">
              <Button
                type="button"
                onClick={handleClose}
                disabled={isSaving}
                className={cn(
                  "bg-[#DC4426] hover:bg-[#c23a1f] h-[50px] w-[149px] rounded-[10px]",
                  "flex items-center justify-center gap-[3px]",
                  "font-['Plus_Jakarta_Sans'] font-bold text-[18px] text-[#F5F2FF]",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSaving}
                className={cn(
                  "bg-[#13D480] hover:bg-[#10b56d] h-[50px] w-[149px] rounded-[10px]",
                  "flex items-center justify-center gap-[3px]",
                  "font-['Plus_Jakarta_Sans'] font-bold text-[18px] text-[#F5F2FF]",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                <Check size={24} />
                {isSaving ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
